<template>
  <div class="min-h-full bg-cream px-3 pt-12 pb-24">
    <ProfilePageHeader title="Настройки Дневника" />
    <nav class="flex flex-col gap-1 mb-8" aria-label="Настройки">
      <RouterLink to="/profile/notifications" class="settings-link-row">
        <span class="flex items-center gap-2.5 flex-1 min-w-0">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="shrink-0 text-ink" aria-hidden="true">
            <path d="M9 19a3 3 0 0 0 6 0M10 3a2 2 0 0 1 4 0M5 10a7 7 0 0 1 14 0v4l2 3v2H3v-2l2-3z" vector-effect="non-scaling-stroke" />
          </svg>
          <span class="font-semibold text-14 leading-tight">Уведомления</span>
        </span>
        <svg aria-hidden="true" class="shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 6l6 6-6 6" /></svg>
      </RouterLink>
      <RouterLink to="/zoya/settings" class="settings-link-row">
        <span class="flex items-center gap-2.5 flex-1 min-w-0">
          <img src="/Zoya_avatar_2_small.jpg" alt="" width="20" height="20" class="w-5 h-5 rounded-full object-cover shrink-0" />
          <span class="font-semibold text-14 leading-tight">Стиль общения с Зоей</span>
        </span>
        <svg aria-hidden="true" class="shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 6l6 6-6 6" /></svg>
      </RouterLink>
      <RouterLink to="/profile/account" class="settings-link-row">
        <span class="flex items-center gap-2.5 flex-1 min-w-0">
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 28 28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" class="shrink-0 text-ink">
            <circle cx="14" cy="14" r="12" vector-effect="non-scaling-stroke"/><circle cx="14" cy="11" r="4" vector-effect="non-scaling-stroke"/><path d="M6 23c0-4 3.6-6 8-6s8 2 8 6" vector-effect="non-scaling-stroke"/>
          </svg>
          <span class="font-semibold text-14 leading-tight">Настройки аккаунта</span>
        </span>
        <svg aria-hidden="true" class="shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 6l6 6-6 6" /></svg>
      </RouterLink>
    </nav>
    <!-- ── Режим использования ── -->
    <div class="mb-8">
      <h2 class="text-20 font-bold text-ink mb-3">Режим</h2>
      <div class="segment-cards">
        <button
          v-for="opt in userTypeOptions"
          :key="opt.val"
          @click="changeUserType(opt.val)"
          class="segment-card"
          :class="{ selected: form.user_type === opt.val }"
        >
          <span class="seg-emoji">{{ opt.emoji }}</span>
          <div class="seg-text">
            <div class="seg-title">{{ opt.title }}</div>
            <div class="seg-desc">{{ opt.desc }}</div>
          </div>
        </button>
      </div>
      <p class="text-13 mt-2.5" style="color: var(--ink-70); line-height: 1.4;">Режим задаёт набор видимых показателей и акценты рекомендаций. Показатели можно изменить вручную.</p>
    </div>

    <!-- ── Что показывать в Дневнике ── -->
    <div class="mb-8">
      <h2 class="text-20 font-bold text-ink mb-3">Что показывать в Дневнике</h2>
      <div class="flex flex-col gap-1.5">
        <label
          v-for="opt in macroToggleOptions"
          :key="opt.key"
          class="relative flex items-center justify-between cursor-pointer rounded-xl overflow-hidden px-3"
          :style="{ height: '36px', background: opt.color || '#E5DFD5' }"
        >
          <span
            class="text-ink leading-none z-[1]"
            style="font-family: Inter, sans-serif; font-size: var(--fs-14); font-weight: 600;"
          >{{ opt.label }}</span>
          <div class="relative shrink-0 ml-3 z-[1]">
            <input type="checkbox" v-model="form.ds[opt.key]" class="sr-only peer" />
            <div class="w-10 h-5 rounded-full transition-colors" :style="{ background: form.ds[opt.key] ? '#41291A' : 'rgba(65,41,26,0.25)' }" />
            <div class="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
          </div>
        </label>

        <!-- Вес блюд и ингредиентов -->
        <div
          class="relative flex items-center justify-between cursor-pointer rounded-xl overflow-hidden px-3"
          style="min-height: 44px; background: #EDE5D8;"
          @click="form.ds.show_weight = !form.ds.show_weight"
        >
          <span class="text-ink z-[1] py-2" style="font-family: Inter, sans-serif; font-size: var(--fs-13); font-weight: 600; line-height: 1.3;">
            Вес блюд и ингредиентов
          </span>
          <div class="relative shrink-0 ml-3 z-[1]">
            <div class="w-10 h-5 rounded-full transition-colors" :style="{ background: form.ds.show_weight ? '#41291A' : 'rgba(65,41,26,0.25)' }" />
            <div class="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform" :class="{ 'translate-x-5': form.ds.show_weight }" />
          </div>
        </div>

        <!-- Голод и сытость -->
        <div
          class="relative flex items-center justify-between cursor-pointer rounded-xl overflow-hidden px-3"
          style="min-height: 44px; background: #EDE5D8;"
          @click="form.ds.show_hunger = !form.ds.show_hunger"
        >
          <span class="flex items-center gap-3 z-[1] py-2" style="min-width: 0;">
            <span class="flex gap-0.5 shrink-0" aria-hidden="true">
              <span v-for="value in [3, 6]" :key="value" class="inline-flex items-center justify-center rounded-full text-13 font-bold text-ink" :style="{ width: '25px', height: '25px', background: SMILEY_COLORS[value], lineHeight: 1 }">{{ value }}</span>
            </span>
            <span class="text-ink" style="font-family: Inter, sans-serif; font-size: var(--fs-13); font-weight: 600; line-height: 1.3;">
              Голод и сытость до/после приема пищи
              <button
                type="button"
                @click.stop="hungerInfoOpen = true"
                class="text-ai underline ml-0.5"
                style="font-size: var(--fs-12); font-weight: 500;"
              >подробнее</button>
            </span>
          </span>
          <div class="relative shrink-0 ml-3 z-[1]">
            <div class="w-10 h-5 rounded-full transition-colors" :style="{ background: form.ds.show_hunger ? '#41291A' : 'rgba(65,41,26,0.25)' }" />
            <div class="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform" :class="{ 'translate-x-5': form.ds.show_hunger }" />
          </div>
        </div>

        <!-- Подсказки Зои в дневнике (services/guide.js) — только у варианта bubbles -->
        <div
          v-if="guideState.variant === 'bubbles'"
          class="relative flex items-center justify-between cursor-pointer rounded-xl overflow-hidden px-3"
          style="min-height: 44px; background: #EDE5D8;"
          @click="guide.setOff(!guideState.off)"
        >
          <span class="flex items-center gap-1.5 z-[1] py-2" style="min-width: 0;">
            <img src="/Zoya_avatar_2_small.jpg" alt="" class="w-5 h-5 rounded-full shrink-0 object-cover" />
            <span class="text-ink" style="font-family: Inter, sans-serif; font-size: var(--fs-13); font-weight: 600; line-height: 1.3;">
              Подсказки Зои в дневнике
            </span>
          </span>
          <div class="relative shrink-0 ml-3 z-[1]">
            <div class="w-10 h-5 rounded-full transition-colors" :style="{ background: !guideState.off ? '#41291A' : 'rgba(65,41,26,0.25)' }" />
            <div class="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform" :class="{ 'translate-x-5': !guideState.off }" />
          </div>
        </div>

        <!-- Дефицит / профицит -->
        <label
          class="relative flex items-center justify-between cursor-pointer rounded-xl overflow-hidden px-3"
          style="min-height: 44px; background: #EDE5D8;"
        >
          <span class="flex items-center gap-3 z-[1] py-2" style="min-width: 0;">
            <span class="grid grid-cols-2 gap-0.5 shrink-0" aria-hidden="true">
              <span class="inline-block w-3.5 h-3.5 rounded-[3px]" style="background: #A6B779;" />
              <span class="inline-block w-3.5 h-3.5 rounded-[3px]" style="background: #e4c9b6;" />
              <span class="inline-block w-3.5 h-3.5 rounded-[3px]" style="background: #EE998C;" />
              <span class="inline-block w-3.5 h-3.5 rounded-[3px]" :style="dayBackground('sample', true, { dayStates: { sample: { complete: false } } })" />
            </span>
            <span class="flex flex-col gap-0.5" style="min-width: 0;">
              <span class="text-ink" style="font-family: Inter, sans-serif; font-size: var(--fs-13); font-weight: 600; line-height: 1.3;">
                Отмечать цветом дни с дефицитом, нормой и профицитом, а также неполные дни в графиках
              </span>
            </span>
          </span>
          <div class="relative shrink-0 ml-3 z-[1]">
            <input type="checkbox" v-model="form.diary_show_kcal_colors" class="sr-only peer" />
            <div class="w-10 h-5 rounded-full transition-colors" :style="{ background: form.diary_show_kcal_colors ? '#41291A' : 'rgba(65,41,26,0.25)' }" />
            <div class="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
          </div>
        </label>
      </div>
    </div>

    <!-- ── Попап: Шкала голода-сытости ── -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="hungerInfoOpen" class="fixed inset-0 z-[90] flex items-end justify-center" @click.self="hungerInfoOpen = false">
          <div class="absolute inset-0 bg-black/40" @click="hungerInfoOpen = false" />
          <div class="relative w-full max-w-[430px] bg-cream rounded-t-2xl shadow-2xl flex flex-col sheet-inset" style="max-height: 80vh">
            <div class="shrink-0 px-5 pt-4 pb-3">
              <div class="w-10 h-1 bg-sand rounded-full mx-auto mb-4" />
              <div class="flex items-center justify-between">
                <h3 class="text-17 font-bold text-ink">Шкала голода-сытости</h3>
                <button @click="hungerInfoOpen = false" class="w-8 h-8 flex items-center justify-center rounded-full text-ink active:bg-line transition-colors shrink-0 ml-2">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>
            <div class="flex-1 overflow-y-auto px-5 pb-8">
              <div class="text-14 text-ink leading-relaxed space-y-3">
                <p>Шкала голода-сытости — это основной инструмент, который используется в осознанном интуитивном питании для того, чтобы оценить свое состояние в момент времени: насколько вы голодны или сыты. На основе этого нужно принять решение — есть сейчас или подождать, остановить прием пищи или продолжать есть.</p>
                <p>Это еще и отличный инструмент самодиагностики: если вы попробовали, и у вас категорически не получается понять, какая же у вас сейчас цифра по шкале, это позволяет предположить нарушенный контакт с телом.</p>
                <p>Пользоваться этой шкалой будет еще удобнее, если представлять ее в виде лифта. Вообразите, что разные уровни голода и сытости - этажи, а наше пищевое поведение — это лифт, который ездит по этажам.</p>
                <p>Первый и второй этажи — сильнейший голод. На эти этажи заезжать вообще нежелательно.</p>
                <p>Верхние этажи — 8, 9, 10 - это чрезмерная сытость, на эти этажи нам тоже не нужно. И для здоровья тела, и для здоровья психики с точки зрения РПП — это опасные этажи, на них тоже оказываться нежелательно.</p>
                <p>Здоровое пищевое поведение по этой шкале находится в диапазоне с 3-го по 7-й этажи.</p>
                <p>Если у вас получается пользоваться шкалой голода-сытости, то полезно будет вести ее в дневнике питания: сначала отмечать цифру до, на каком уровне голода-сытости вы были, а потом — на какой этаж вы переместились после того, как поели.</p>
                <p>Хорошо, если это будет выглядеть так: «3-6», или «4-7». По этим записям в дневнике вам сразу станет понятно, где есть переедание, а где нет.</p>
              </div>
              <button @click="hungerInfoOpen = false" class="mt-4 w-full py-3 rounded-2xl bg-ink text-cream text-15 font-semibold">
                Понятно
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- ── Теги для Избранного ── -->
    <div class="mb-8">
      <h2 class="text-20 font-bold text-ink mb-1">Теги для Избранного</h2>
      <p class="text-13 mb-3" style="color: var(--ink-70); line-height: 1.4;">
        Отмечайте тегами блюда и можете фильтровать по ним при добавлении из Избранного
      </p>
      <div
        class="relative flex items-center justify-between cursor-pointer rounded-xl overflow-hidden px-3 mb-3"
        style="min-height: 44px; background: #EDE5D8;"
        @click="form.ds.show_fav_tags = !form.ds.show_fav_tags"
      >
        <span class="text-ink py-2" style="font-family: Inter, sans-serif; font-size: var(--fs-13); font-weight: 600; line-height: 1.3;">Показывать теги</span>
        <div class="relative shrink-0 ml-3">
          <div class="w-10 h-5 rounded-full transition-colors" :style="{ background: form.ds.show_fav_tags ? '#41291A' : 'rgba(65,41,26,0.25)' }" />
          <div class="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform" :class="{ 'translate-x-5': form.ds.show_fav_tags }" />
        </div>
      </div>

      <template v-if="form.ds.show_fav_tags">
        <div class="flex flex-wrap gap-1.5 mb-3">
          <span v-for="tag in favTags.tags" :key="tag" class="tag-chip">
            {{ tag }}
            <button type="button" class="tag-x" :aria-label="`Удалить тег ${tag}`" :disabled="tagBusy" @click="removeTag(tag)">
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M2 2l8 8M10 2l-8 8"/></svg>
            </button>
          </span>
          <span v-if="favTags.loaded && !favTags.tags.length" class="text-13" style="color: var(--ink-70);">Тегов пока нет — добавьте первый.</span>
        </div>
        <form class="flex gap-2" @submit.prevent="addTag">
          <input
            v-model="newTag" type="text" :maxlength="favTags.maxLength" enterkeyhint="done"
            placeholder="мой тег"
            class="flex-1 min-w-0 h-11 px-3 rounded-xl bg-white border border-sand text-ink outline-none"
            style="font-family: Inter, sans-serif; font-size: 16px;"
          />
          <button type="submit" :disabled="!newTag.trim() || tagBusy || tagsFull"
                  class="h-11 px-4 rounded-xl bg-ink text-cream text-14 font-semibold disabled:opacity-40">Добавить</button>
        </form>
        <p class="text-12 mt-1.5" :style="{ color: tagError ? 'var(--error)' : 'var(--ink-70)' }">
          {{ tagError || (tagsFull ? `Больше ${favTags.maxTags} тегов добавить нельзя.` : `До ${favTags.maxLength} знаков. Удалённый тег снимается со всех блюд.`) }}
        </p>
        <button v-if="tagsChanged" type="button" class="text-13 text-ai underline mt-2" :disabled="tagBusy" @click="resetTags">Вернуть стандартный набор</button>
      </template>
    </div>

    <!-- ── Модель для распознавания фото ── -->
    <div class="mb-8">
      <h2 class="text-20 font-bold text-ink mb-3">Модель для распознавания еды</h2>
      <div class="segment-cards">
        <button
          v-for="opt in photoModelOptions"
          :key="String(opt.val)"
          @click="form.ds.photo_max_quality = opt.val"
          class="segment-card"
          :class="{ selected: !!form.ds.photo_max_quality === opt.val }"
        >
          <span class="seg-emoji">{{ opt.emoji }}</span>
          <div class="seg-text">
            <div class="seg-title">{{ opt.title }}</div>
            <div class="seg-desc">{{ opt.desc }}</div>
          </div>
        </button>
      </div>
    </div>

    <!-- Назад в Профиль -->
    <button
      @click="$router.push('/profile')"
      class="w-full py-3 rounded-2xl bg-ink text-cream text-15 font-semibold mb-3"
    >
      Назад в Профиль
    </button>

  </div>
</template>

<script setup>
import ProfilePageHeader from '../components/ProfilePageHeader.vue'
import { ref, reactive, watch, computed, onMounted } from 'vue'
import { useAuthStore } from '../store/auth.js'
import { useFavTagsStore } from '../store/favTags.js'
import { dayBackground } from '../utils/kcalDayColor.js'
import { SMILEY_COLORS } from '../utils/smileyUtils.js'
import guide, { state as guideState } from '../services/guide.js'

const authStore = useAuthStore()
const hungerInfoOpen = ref(false)


const DIARY_DEFAULTS = {
  control:  { show_kcal: true, show_protein: true, show_fat: true, show_carbs: true, show_fiber: true, show_water: true, show_vitamins: true, show_hunger: false, show_weight: true, photo_max_quality: false, show_fav_tags: true },
  training: { show_kcal: true, show_protein: true, show_fat: true, show_carbs: true, show_fiber: true, show_water: true, show_vitamins: false, show_hunger: false, show_weight: true, photo_max_quality: false, show_fav_tags: true },
  rpp:      { show_kcal: false, show_protein: false, show_fat: false, show_carbs: false, show_fiber: false, show_water: false, show_vitamins: false, show_hunger: true, show_weight: true, photo_max_quality: false, show_fav_tags: true },
}

const photoModelOptions = [
  { val: false, emoji: '⚖️', title: 'Оптимальный баланс',
    desc: 'Скорость и высокое качество. Распознавание занимает около пяти секунд' },
  { val: true,  emoji: '🎯', title: 'Максимально точно',
    desc: 'В ущерб скорости: распознавание занимает около пятнадцати секунд' },
]

const userTypeOptions = [
  { val: 'rpp',      emoji: '🫶',  title: 'Бережно', desc: 'Для тех, кто столкнулся с признаками РПП' },
  { val: 'control',  emoji: '📊',  title: 'Контроль', desc: 'Для тех, кто получает удовольствие от точных данных' },
  { val: 'training', emoji: '🏋️', title: 'Спорт', desc: 'Для тех, кто сфокусирован на наращивании мышц и тренировках' },
]

const macroToggleOptions = [
  { key: 'show_kcal', label: 'Ккал', color: '#EE998C' },
  { key: 'show_protein', label: 'Белки', color: '#9FC2C1' },
  { key: 'show_fat', label: 'Жиры', color: '#EBC06D' },
  { key: 'show_carbs', label: 'Углеводы', color: '#EEAD77' },
  { key: 'show_fiber', label: 'Клетчатка', color: '#A6B779' },
  { key: 'show_water', label: 'Вода', color: '#ABDEEB' },
  { key: 'show_vitamins', label: 'Витамины и минералы', color: '#EDE5D8' },
]

const initDs = authStore.user?.diary_settings || DIARY_DEFAULTS[authStore.user?.user_type || 'control']

const form = reactive({
  user_type: authStore.user?.user_type || 'control',
  ds: { ...initDs },
  diary_show_kcal_colors: authStore.user?.diary_show_kcal_colors ?? true,
})

function changeUserType(type) {
  form.user_type = type
  // Режим переопределяет то, что показывать в дневнике, но не выбор модели:
  // он про скорость распознавания и к бережности или контролю отношения не имеет.
  const keepPhoto = form.ds.photo_max_quality
  const keepTags = form.ds.show_fav_tags
  Object.assign(form.ds, DIARY_DEFAULTS[type])
  form.ds.photo_max_quality = keepPhoto
  form.ds.show_fav_tags = keepTags
  form.diary_show_kcal_colors = type === 'control'
}

// ── Теги для Избранного: правки списка сохраняются сразу ──
const DEFAULT_FAV_TAGS = ['Завтрак', 'Обед', 'Ужин', 'Перекус', 'Десерт', 'Суп', 'Салат', 'Гарнир', 'Мясо', 'Птица', 'Рыба', 'Напитки']
const favTags = useFavTagsStore()
const newTag = ref('')
const tagBusy = ref(false)
const tagError = ref('')
onMounted(() => favTags.load())
const tagsFull = computed(() => favTags.tags.length >= favTags.maxTags)
const tagsChanged = computed(() => favTags.loaded && favTags.tags.join('|') !== DEFAULT_FAV_TAGS.join('|'))

async function saveTags(list) {
  tagBusy.value = true
  tagError.value = ''
  try { await favTags.save(list); return true }
  catch (err) { tagError.value = err.response?.data?.error || 'Не получилось сохранить — попробуйте ещё раз'; return false }
  finally { tagBusy.value = false }
}
async function addTag() {
  const tag = newTag.value.replace(/[\s,]+/g, ' ').trim()
  if (!tag) return
  if (favTags.tags.some(t => t.toLowerCase() === tag.toLowerCase())) { tagError.value = 'Такой тег уже есть'; return }
  if (await saveTags([...favTags.tags, tag])) newTag.value = ''
}
function removeTag(tag) { return saveTags(favTags.tags.filter(t => t !== tag)) }
function resetTags() { return saveTags(DEFAULT_FAV_TAGS) }

// Автосохранение с debounce
let debounceTimer = null
watch(form, () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(async () => {
    try {
      await authStore.updateProfile({
        user_type: form.user_type,
        diary_settings: { ...form.ds },
        diary_show_kcal_colors: form.diary_show_kcal_colors,
      })
    } catch { /* silent */ }
  }, 800)
}, { deep: true })
</script>

<style scoped>

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 4px 0 13px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid var(--line);
  color: var(--ink);
  font-family: Inter, sans-serif;
  font-size: var(--fs-13);
  font-weight: 500;
  white-space: nowrap;
}
.tag-x {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: var(--ink-60);
}
.tag-x:active { background: var(--line); color: var(--ink); }

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.segment-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.segment-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px;
  border: 2px solid rgba(65, 41, 26, 0.08);
  border-radius: 18px;
  background: #FFFCF3;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: all 0.2s;
}
.segment-card:active {
  transform: scale(0.98);
}
.segment-card.selected {
  border-color: var(--ink);
  background: #41291A;
}
.segment-card.selected .seg-title,
.segment-card.selected .seg-desc {
  color: var(--cream);
}
.seg-emoji {
  font-size: var(--fs-28);
  flex-shrink: 0;
  line-height: 1;
  margin-top: 2px;
}
.seg-text {
  flex: 1;
  min-width: 0;
}
.seg-title {
  font-size: var(--fs-17);
  font-weight: 600;
  line-height: 1.2;
  color: var(--ink);
}
.seg-desc {
  font-size: 13.5px;
  color: var(--ink-70);
  margin-top: 4px;
  line-height: 1.4;
}
</style>
